import { gql } from '../../../graphql/client';
import {
  M_INSERT_VIEW_SOURCE,
  M_INSERT_VIEW_STYLE,
  M_LINK_VIEW_SOURCE,
  M_LINK_VIEW_STYLE,
  M_UPSERT_VIEW,
  M_UPSERT_VIEW_SOURCE,
  M_UPSERT_VIEW_STYLE,
} from '../../../graphql/operations';
import type { View, ViewSource, ViewStyle } from '../../../types/forge';

/**
 * Save pipeline — port of Burrow's `_saveData`.
 *
 * Phase 2 simplification: no externalize-content extraction yet (Phase 3+).
 * The pipeline is:
 *   1. If viewStyle has no id → insert + link
 *      else → upsert by id
 *   2. If viewSource has no id → insert + link
 *      else → upsert by id
 *   3. Update View top-level fields (name, Type)
 *
 * Phase 5+ extends with `extractContent` (literal → ${content.x}) and
 * batched externalizeContent (insert_external_content + insert_ViewSource).
 */

export interface SaveViewInput {
  viewId: string;
  patch: Partial<View>;
}

export interface SaveResult {
  ok: true;
  viewId: string;
}

export async function saveView(input: SaveViewInput): Promise<SaveResult> {
  const { viewId, patch } = input;

  // 1. ViewStyle
  if (patch.viewStyle) {
    const style = patch.viewStyle as ViewStyle;
    if (style.id) {
      await gql(M_UPSERT_VIEW_STYLE, { id: style.id, properties: style.properties });
    } else {
      const inserted = await gql<{ insert_ViewStyle_one: ViewStyle }>(
        M_INSERT_VIEW_STYLE,
        { properties: style.properties },
      );
      await gql(M_LINK_VIEW_STYLE, {
        viewId,
        styleId: inserted.insert_ViewStyle_one.id,
      });
    }
  }

  // 2. ViewSource
  if (patch.viewSource) {
    const src = patch.viewSource as ViewSource;
    const baseVars = {
      type: src.type,
      value: src.value ?? null,
      url: src.url ?? null,
      method: src.method ?? null,
      query: src.query ?? null,
      variables: src.variables ?? null,
    };
    if (src.id) {
      await gql(M_UPSERT_VIEW_SOURCE, { id: src.id, ...baseVars });
    } else {
      const inserted = await gql<{ insert_ViewSource_one: ViewSource }>(
        M_INSERT_VIEW_SOURCE,
        baseVars,
      );
      await gql(M_LINK_VIEW_SOURCE, {
        viewId,
        sourceId: inserted.insert_ViewSource_one.id,
      });
    }
  }

  // 3. Top-level View fields — build _set from only the fields actually changing.
  const setObj: Record<string, unknown> = {};
  if (patch.name !== undefined) setObj.name = patch.name;
  if (patch.Type !== undefined) setObj.Type = patch.Type;
  if (Object.keys(setObj).length > 0) {
    await gql(M_UPSERT_VIEW, { id: viewId, set: setObj });
  }

  return { ok: true, viewId };
}
